import * as React from 'react'
import { useMemo } from 'react'

import { useTranslation } from 'react-i18next'

import { Box, Flex, Spacer, Stack } from '@chakra-ui/react'

import {
  Label,
  SecondaryButton,
  Select,
  SelectOption,
  Term,
  TextButton,
} from '@enechain/ecloud-designsystem'
import { FaCircleXmarkSolid } from '@enechain/ecloud-icons'

import DebounceTextInput from '~/components/parts/DebounceTextInput'
import { MemberStatus } from '~/gen/proto/bff/v1/model_pb'
import { dict } from '~/pages/Members/dict'
import {
  EffectivePlan,
  availableRoles,
} from '~/pages/Members/utils/effectivePlans'
import {
  MemberFilterCondition,
  emptyMemberFilterCondition,
} from '~/pages/Members/utils/filterMembers'

// Statuses selectable in the status dropdown, in display order.
const STATUS_OPTIONS: MemberStatus[] = [
  MemberStatus.ACTIVE,
  MemberStatus.INVITED,
  MemberStatus.SUSPENDED,
  MemberStatus.DELETED,
]

type Props = {
  // The organization's effective contract plans; the plan/role options are
  // derived from them.
  plans: EffectivePlan[]
  condition: MemberFilterCondition
  onChange: (condition: MemberFilterCondition) => void
  onExportCsv: () => void
  isExportDisabled: boolean
}

const MemberListFilter: React.FC<Props> = ({
  plans,
  condition,
  onChange,
  onExportCsv,
  isExportDisabled,
}) => {
  const { t, i18n } = useTranslation()
  const isEnglish = i18n.language === 'en'

  const planOptions: SelectOption[] = plans.map((plan) => ({
    value: plan.planCode,
    label: isEnglish ? plan.engPlanName : plan.planName,
  }))

  // Roles are matched by Japanese role name: same-named roles across plans
  // are offered as a single option, and the name key stays stable when the
  // UI language changes. When plans are selected, only their roles are
  // offered.
  const roleOptions: SelectOption[] = useMemo(
    () =>
      availableRoles(plans, condition.planCodes).map((role) => ({
        value: role.name,
        // engName can be empty; fall back to the Japanese name rather than
        // showing a blank option.
        label: isEnglish && role.engName !== '' ? role.engName : role.name,
      })),
    [plans, condition.planCodes, isEnglish],
  )

  const statusOptions: SelectOption[] = STATUS_OPTIONS.map((status) => ({
    value: status.toString(),
    label: dict.status(status),
  }))

  const selectedPlans = planOptions.filter((option) =>
    condition.planCodes.includes(option.value),
  )
  const selectedRoles = roleOptions.filter((option) =>
    condition.roleNames.includes(option.value),
  )
  const selectedStatus =
    condition.status !== undefined
      ? (statusOptions.find(
          (option) => option.value === condition.status?.toString(),
        ) ?? null)
      : null

  const isRoleSelectDisabled = condition.planCodes.length === 0

  const onPlanChange = (selected: readonly SelectOption[]): void => {
    const planCodes = selected.map((option) => option.value)
    // Roles are only selectable while at least one plan is selected, so
    // removing the last plan clears them. Otherwise drop selected roles that
    // do not belong to the newly selected plans, so they cannot keep
    // filtering invisibly after leaving the options.
    const allowed = new Set(
      availableRoles(plans, planCodes).map((role) => role.name),
    )
    onChange({
      ...condition,
      planCodes,
      roleNames:
        planCodes.length === 0
          ? []
          : condition.roleNames.filter((name) => allowed.has(name)),
    })
  }
  const onRoleChange = (selected: readonly SelectOption[]): void => {
    onChange({ ...condition, roleNames: selected.map((o) => o.value) })
  }
  const onStatusChange = (selected: SelectOption | null): void => {
    onChange({
      ...condition,
      status:
        selected != null ? (Number(selected.value) as MemberStatus) : undefined,
    })
  }
  const onKeywordChange = (value: string): void => {
    onChange({ ...condition, keyword: value })
  }
  const onClear = (): void => {
    onChange(emptyMemberFilterCondition)
  }

  return (
    <Box padding="6" borderRadius="8px" backgroundColor="Surface.secondary">
      <Stack gap="4">
        {/* Wrap so the filters stay inside the card on narrow windows
            instead of overflowing its background. */}
        <Flex alignItems="flex-end" flexWrap="wrap" gap="4">
          {/* Cap the multi selects at a share of the bar width so selected
              chips start wrapping inside them early instead of stretching
              the whole row. Term swallows style props, so the widths live
              on wrapper Boxes. */}
          <Box minWidth="200px" maxWidth="max(200px, 25%)">
            <Term>
              <Label>{t('pages.members.filter.plan')}</Label>
              <Select<SelectOption, true>
                isMulti
                options={planOptions}
                onChange={onPlanChange}
                selectedOptions={selectedPlans}
                placeholder={t('pages.members.filter.all')}
              />
            </Term>
          </Box>
          <Box minWidth="200px" maxWidth="max(200px, 25%)">
            <Term>
              <Label>{t('pages.members.filter.role')}</Label>
              {/* Same-named roles across plans are matched per plan, so a
                  role filter without a plan is ambiguous; require selecting
                  a plan first. */}
              <Select<SelectOption, true>
                isMulti
                options={roleOptions}
                onChange={onRoleChange}
                selectedOptions={selectedRoles}
                isDisabled={isRoleSelectDisabled}
                placeholder={t(
                  isRoleSelectDisabled
                    ? 'pages.members.filter.selectPlanFirst'
                    : 'pages.members.filter.all',
                )}
                // Role count can be 30+; the in-dropdown search covers it.
                isSearchable
              />
            </Term>
          </Box>
          <Term>
            <Label>{t('pages.members.filter.status')}</Label>
            <Box minWidth="180px">
              <Select<SelectOption, false>
                isMulti={false}
                isClearable
                options={statusOptions}
                onChange={onStatusChange}
                selectedOptions={selectedStatus}
                placeholder={t('pages.members.filter.all')}
              />
            </Box>
          </Term>
          {/* Keep the keyword input and the CSV button at full size even
              when the plan/role chips fill the row: the selects shrink and
              wrap their chips instead. Term swallows style props, so the
              flexShrink lives on a wrapper Box. */}
          <Box flexShrink={0}>
            <Term>
              <Label>{t('pages.members.filter.keyword')}</Label>
              <DebounceTextInput
                value={condition.keyword}
                onChange={onKeywordChange}
                placeholder={t('pages.members.filter.keywordPlaceholder')}
              />
            </Term>
          </Box>
          <Spacer />
          <SecondaryButton
            flexShrink={0}
            onClick={onExportCsv}
            disabled={isExportDisabled}
          >
            {t('pages.members.csv.export')}
          </SecondaryButton>
        </Flex>
        <Box>
          <TextButton
            onClick={onClear}
            color="Text.accentPrimary"
            _hover={{ backgroundColor: 'transparent' }}
            _active={{ backgroundColor: 'transparent' }}
            // BaseButton hardcodes a focus ring after the props spread, so it
            // can only be disabled through sx (highest style precedence).
            css={{ '&:focus': { boxShadow: 'none' } }}
            leftIcon={
              // TextButton colors the leftIcon wrapper on its own, so set the
              // color on the icon itself to override it.
              <FaCircleXmarkSolid boxSize="16px" color="Object.accentPrimary" />
            }
          >
            {t('pages.members.filter.clear')}
          </TextButton>
        </Box>
      </Stack>
    </Box>
  )
}

export default React.memo(MemberListFilter)
